import type { AssignmentStatus, DisplayStatus, QuizAssignment, QuizStatus } from '../types';

export type TimestampLike = { toMillis?: () => number; toDate?: () => Date } | Date | number | null | undefined;

/** Epoch millis from a Firestore Timestamp, Date, number, or null. */
export function toMillis(value: TimestampLike): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  return null;
}

const nowMs = (now: Date | number): number => (typeof now === 'number' ? now : now.getTime());

/** True while the assignment's start date is still in the future. */
export function isScheduled(assignment: Pick<QuizAssignment, 'availableAt'>, now: Date | number): boolean {
  const at = toMillis(assignment.availableAt as TimestampLike);
  return at !== null && at > nowMs(now);
}

export function displayStatus(
  assignment: Pick<QuizAssignment, 'status' | 'dueAt' | 'availableAt'>,
  quizStatus: QuizStatus | undefined,
  now: Date
): DisplayStatus {
  if (assignment.status === 'completed') return 'completed';
  if (assignment.status === 'missed') return 'missed';
  if (quizStatus === 'closed' || quizStatus === 'archived') return 'missed';
  if (assignment.status === 'assigned' && isScheduled(assignment, now)) return 'scheduled';
  const due = toMillis(assignment.dueAt as TimestampLike);
  if (due !== null && due < now.getTime()) return 'overdue';
  return assignment.status;
}

const OPEN: AssignmentStatus[] = ['assigned', 'in_progress'];

/** Status-only check: not yet completed or missed (ignores start dates). */
export function hasOpenStatus(assignment: Pick<QuizAssignment, 'status'>): boolean {
  return OPEN.includes(assignment.status);
}

/** Open = actionable by the assignee right now: open status and the start date has passed. */
export function isOpenAssignment(
  assignment: Pick<QuizAssignment, 'status' | 'availableAt'>,
  now: Date | number
): boolean {
  return hasOpenStatus(assignment) && !isScheduled(assignment, now);
}

/**
 * The assignments a close/archive batch must mark 'missed': every still-open one, including
 * those whose start date has not arrived yet.
 */
export function assignmentsToMiss<T extends Pick<QuizAssignment, 'status'>>(assignments: T[]): T[] {
  return assignments.filter(hasOpenStatus);
}

/** Millis until the soonest future availableAt among open assignments, or null if none. */
export function nextAvailabilityChange(
  assignments: Array<Pick<QuizAssignment, 'status' | 'availableAt'>>,
  now: Date | number
): number | null {
  const current = nowMs(now);
  let soonest: number | null = null;
  for (const a of assignments) {
    if (!hasOpenStatus(a)) continue;
    const at = toMillis(a.availableAt as TimestampLike);
    if (at === null || at <= current) continue;
    if (soonest === null || at < soonest) soonest = at;
  }
  return soonest === null ? null : soonest - current;
}
